import React, { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { getLogger, useEffectOnce } from '@chilecompra/react-kit'
import {
  Card,
  Tab,
  Tabs,
  Table,
  TableCell,
  TableRow,
  Typography,
  useAutocomplete,
  useSelect
} from '@chilecompra/react-kit/components'
import generateYearsBetween from '../../modules/utils/years'
import { REACT_APP_DATA_SECONDARY, REACT_APP_MP_SEARCH } from '../../config/settings/environment'
import {
  onGetEntityReportOptionsThunk,
  onGetEntityReportBuyerOptionsThunk
} from './DownloadOrdersAndBiddingsSection.actions'
import { useScrollToTop } from '../../components/Navigation/ScrollToTop.context'
import BuildDialog from './DownloadOrdersAndBiddingsSection.build'
import Massive from './DownloadOrdersAndBiddingsSection.massive'
import PreviewDialog from './DownloadOrdersAndBiddingsSection.preview'
import Reports from './DownloadOrdersAndBiddingsSection.reports'
import DownloadsNavButton from '../../components/Navigation/DownloadsNavButton'
import {
  DialogLink,
  Description,
  GridContainer,
  GridItem,
  Root,
  Strong,
  TableContainer,
  Title
} from './DownloadOrdersAndBiddingsSection.styles'

const logger = getLogger('DOWNLOADS_CLIPBOARD')
const START_YEAR = 2007

/**
 * The DownloadOrdersAndBiddingsSection's container.
 */
const DownloadOrdersAndBiddingsSection = () => {
  const dispatch = useDispatch()
  const { hash, state: locationState } = useLocation()
  const { biddingOptions, orderOptions, buyerOptions } = useSelector(state => state.downloadOrdersAndBiddingsSection)
  const { endYear } = useSelector(state => state.topNavbar)
  const scrollToElement = useScrollToTop()
  const [openBuildDialog, setOpenBuildDialog] = useState(false)
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false)
  const [originBuildDialog, setOriginBuildDialog] = useState(null)
  const [originPreviewDialog, setOriginPreviewDialog] = useState(null)
  const {
    option: searchBuyerOption,
    value: searchBuyerValue,
    onChange: handleChangeSearchBuyer,
    onReset: resetSearchBuyer,
    onSelect: handleSelectSearchBuyer,
    setOption: setSearchBuyerOption,
    setValue: setSearchBuyerValue
  } = useAutocomplete({
    changeCallback: updatedValue =>
      updatedValue.length >= 3 && dispatch(onGetEntityReportBuyerOptionsThunk({ term: updatedValue })),
    selectCallback: updatedOption => dispatch(onGetEntityReportOptionsThunk({ id: updatedOption?.value }))
  })
  const { value: searchSectorValue, onChange: handleChangeSearchSector, onReset: resetSearchSector } = useSelect()
  const { value: searchProviderValue, onChange: handleChangeSearchProvider, onReset: resetSearchProvider } = useSelect()
  const {
    value: searchTypeValue,
    onChange: handleChangeSearchType,
    onReset: resetSearchType
  } = useSelect({
    initialValue: 'entcode',
    changeCallback: () => {
      resetReportType()
      resetSearchBuyer()
      resetSearchProvider()
      resetSearchSector()
      resetSemester()
      resetYear()
    }
  })
  const {
    value: reportTypeValue,
    onChange: handleChangeReportType,
    onReset: resetReportType
  } = useSelect({
    changeCallback: () => {
      resetSemester()
      resetYear()
    }
  })
  const {
    value: yearValue,
    onChange: handleChangeYear,
    onReset: resetYear
  } = useSelect({
    changeCallback: () => resetSemester()
  })
  const { value: semesterValue, onChange: handleChangeSemester, onReset: resetSemester } = useSelect()
  const { value: monthValue, onChange: handleChangeMonth, onReset: resetMonth } = useSelect({ initialValue: '01' })
  const handleChangeTab = useCallback(() => {
    resetMonth()
    resetReportType()
    resetSearchBuyer()
    resetSearchProvider()
    resetSearchSector()
    resetSearchType()
    resetSemester()
    resetYear()
  }, [])
  const handleCloseBuildDialog = useCallback(() => {
    setOpenBuildDialog(false)
    setOriginBuildDialog(null)
  }, [])
  const handleClosePreviewDialog = useCallback(() => {
    setOpenPreviewDialog(false)
    setOriginPreviewDialog(null)
  }, [])
  const handleOpenBuildDialog = updatedOrigin => () => {
    setOpenBuildDialog(true)
    setOriginBuildDialog(updatedOrigin)
  }
  const handleOpenPreviewDialog = updatedOrigin => () => {
    setOpenPreviewDialog(true)
    setOriginPreviewDialog(updatedOrigin)
  }
  const handleClipboardFallback = useCallback(content => {
    const textAreaElement = document.createElement('textarea')

    textAreaElement.value = content
    textAreaElement.style.top = '0'
    textAreaElement.style.left = '0'
    textAreaElement.style.position = 'fixed'

    document.body.appendChild(textAreaElement)
    textAreaElement.focus()
    textAreaElement.select()

    try {
      document.execCommand('copy')
    } catch (error) {
      logger.error('Clipboard Fallback: Unable to copy', error)
    }

    document.body.removeChild(textAreaElement)
  }, [])
  const handleDoubleClickClipboard = content => () => {
    if (!window.navigator.clipboard) {
      return handleClipboardFallback(content)
    }

    window.navigator.clipboard
      .writeText(content)
      .then(() => logger.info('Clipboard: copying to clipboard was successful'))
      .catch(error => logger.error('Clipboard: could not copy content', error))
  }
  const handleClickDownload = (fileName, url) => () => {
    const anchorElement = document.createElement('a')

    anchorElement.setAttribute('download', fileName)
    anchorElement.setAttribute('href', url)
    anchorElement.setAttribute('target', '_blank')
    anchorElement.click()
  }
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, START_YEAR), [endYear])

  useEffectOnce(() => {
    scrollToElement(hash, 1000)
  })

  useEffectOnce(() => {
    if (!locationState) {
      return
    }

    setSearchBuyerOption({ name: locationState?.name, value: locationState?.idEntity })
    setSearchBuyerValue(locationState?.name)
    dispatch(onGetEntityReportOptionsThunk({ id: locationState?.idEntity }))
  })

  return (
    <Root>
      <GridContainer padding="0 0 26px">
        <DownloadsNavButton />
        <GridItem xs={10} sm={11}>
          <Title>Órdenes de compra y licitaciones (incluye ofertas)</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 43px">
        <GridItem xs={12}>
          <Description>
            Accede al registro de órdenes de compra y licitaciones asociadas a cada organismo comprador, sector del
            Estado y proveedores según su tamaño de empresa. Los descargables de las licitaciones incluyen las ofertas
            recibidas cada proceso.
            <br />
            <br />
            La cantidad de información puede variar según sea el caso:
            <br />
            <br />
          </Description>
          <TableContainer>
            <Table>
              <TableRow enableHead>
                <TableCell width="22%">Proceso</TableCell>
                <TableCell>Información</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Descarga de Reportes</TableCell>
                <TableCell>
                  La cantidad de información es semestral y puede variar según sea el caso. Los archivos comprimidos
                  tienen un tamaño entre 10 KB y 100 MB. Esta condición, y el flujo de datos de la banda ancha
                  utilizada, determinará el tiempo de descarga. El alcance de los archivos descargables abarca desde el
                  año 2015 a la actualidad con un mes de desfase con respecto a los datos en la base de datos
                  productiva. Esta actualización se realiza <Strong>todos los meses</Strong>.
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Descarga Masiva</TableCell>
                <TableCell>
                  La cantidad de información mensual puede variar según sea el caso. Los archivos comprimidos tienen un
                  tamaño entre 30 MB y 150 MB. Esta condición, y el flujo de datos de la banda ancha utilizada,
                  determinará el tiempo de descarga. El alcance de los archivos descargables abarca desde el año 2007 a
                  la actualidad con un día de desfase con respecto a los datos en la base de datos productiva. Esta
                  actualización se realiza
                  <Strong> todos los días entre las 12:00 a 14:00 horas</Strong>.
                </TableCell>
              </TableRow>
            </Table>
          </TableContainer>
          <Card color="primary" margin="24px 0">
            <Typography fontSize="14px" variant="body2">
              Por transparencia en las compras públicas, los archivos descargables de órdenes de compra contienen todas
              las órdenes de compra en los estados enviada al proveedor, en proceso, aceptada, solicitud de cancelación
              y recepción conforme, incluso aquellas que son excluidas del procesamiento efectuado por ChileCompra, con
              la finalidad de obtener la fuente de información oficial a partir de la que se construyen las cifras
              oficiales de compras públicas afectas a la Ley N°19.886.
              <Strong>
                Las órdenes de compra son excluidas desde nuestra fuente de información oficial por tener errores en los
                montos o en el tipo de moneda
              </Strong>
              . Por lo tanto, al hacer cálculos sobre los datos íntegros, las cifras pueden diferir de los datos
              procesados. Para obtener el listado de aquellas órdenes de compra excluidas en los procesamientos del
              ChileCompra, puedes revisar la sección de{' '}
              <DialogLink href={REACT_APP_DATA_SECONDARY}>datos complementarios</DialogLink>.
            </Typography>
          </Card>
        </GridItem>
      </GridContainer>
      <GridContainer id="DescargaReportes" />
      <GridContainer>
        <GridItem xs={12}>
          <Tabs value="reports" onChange={handleChangeTab}>
            <Tab id="reports" label="descarga de reportes">
              <Reports
                biddingOptions={biddingOptions}
                orderOptions={orderOptions}
                reportTypeValue={reportTypeValue}
                searchBuyerOption={searchBuyerOption}
                searchBuyerOptions={buyerOptions}
                searchBuyerValue={searchBuyerValue}
                searchProviderValue={searchProviderValue}
                searchSectorValue={searchSectorValue}
                searchTypeValue={searchTypeValue}
                semesterValue={semesterValue}
                yearValue={yearValue}
                yearsOptions={yearsOptions}
                onChangeReportType={handleChangeReportType}
                onChangeSearchBuyer={handleChangeSearchBuyer}
                onChangeSearchProvider={handleChangeSearchProvider}
                onChangeSearchSector={handleChangeSearchSector}
                onChangeSearchType={handleChangeSearchType}
                onChangeSemester={handleChangeSemester}
                onChangeYear={handleChangeYear}
                onClickDownload={handleClickDownload}
                onDoubleClickClipboard={handleDoubleClickClipboard}
                onOpenBuildDialog={handleOpenBuildDialog('reports')}
                onOpenPreviewDialog={handleOpenPreviewDialog('reports')}
                onSelectSearchBuyer={handleSelectSearchBuyer}
              />
            </Tab>
            <Tab id="massive-by-url" label="descarga masiva por url">
              <Massive
                monthValue={monthValue}
                reportTypeValue={reportTypeValue}
                yearValue={yearValue}
                yearsOptions={yearsOptions}
                onChangeMonth={handleChangeMonth}
                onChangeReportType={handleChangeReportType}
                onChangeYear={handleChangeYear}
                onDoubleClickClipboard={handleDoubleClickClipboard}
                onClickDownload={handleClickDownload}
                onOpenBuildDialog={handleOpenBuildDialog('massive')}
                onOpenPreviewDialog={handleOpenPreviewDialog('massive')}
              />
            </Tab>
          </Tabs>
        </GridItem>
        <GridItem xs={12}>
          <Card color="primary" margin="24px 0">
            <Typography fontSize="14px" variant="body2">
              Si deseas descargar las órdenes de compra de algún proveedor en específico, puedes hacerlo a través de la{' '}
              <DialogLink href={REACT_APP_MP_SEARCH}>herramienta</DialogLink> disponible en Mercado Público.
            </Typography>
          </Card>
        </GridItem>
      </GridContainer>
      <BuildDialog open={openBuildDialog} origin={originBuildDialog} onClose={handleCloseBuildDialog} />
      <PreviewDialog open={openPreviewDialog} origin={originPreviewDialog} onClose={handleClosePreviewDialog} />
    </Root>
  )
}

export default DownloadOrdersAndBiddingsSection
